import React, { useContext, useEffect, useState } from "react"
import { Button, Div, Text } from "atomize"
import { DateTime } from "luxon"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faRotateLeft, faTrashCan } from "@fortawesome/free-solid-svg-icons"
import Container from "../atoms/Container"
import Divider from "../atoms/Divider"
import SecondaryButton from "../buttons/SecondaryButton"
import EmptyState from "../atoms/EmptyState"
import { theme } from "../settings/config"
import { modalContext } from "../providers/modal.provider"
import { profileContext } from "../providers/profile.provider"
import { schoolContext } from "../providers/school.provider"
import { sharedDataContext } from "../providers/sharedData.provider"
import { subscribeToLessonDeletions } from "../utils/firestore.utils"
import { getPaymentMethodName, managesLessonsOf, managesSchool } from "../utils/func.utils"
import { formatAmount } from "../utils/billing.utils"
import {
	deletionTitle,
	formatEditTime,
	groupLessonDeletions,
	reinsertLessonData,
} from "../utils/authorship.utils"

const toDateTime = timestamp =>
	timestamp?.toDate ? DateTime.fromJSDate(timestamp.toDate()) : null

// Le lezioni eliminate del circolo, la più recente in cima: chi le ha tolte,
// quando, e cosa c'era dentro. Chi gestisce il circolo le vede tutte; un
// maestro quelle delle sue lezioni tolte da altri. Non ripristina niente da
// solo: "Reinserisci" riapre il modulo della lezione già compilato con
// quello che c'era, e chi lo apre controlla e salva.
function LessonDeletionsModal({ closeModal }) {
	const Modal = useContext(modalContext)
	const { coach } = useContext(profileContext)
	const { getSelectedSchool, selectedSchool } = useContext(schoolContext)
	const { getLessonType, getPaymentMethod } = useContext(sharedDataContext)

	const [deletions, setDeletions] = useState(null)
	const [failed, setFailed] = useState(false)
	const [expandedKey, setExpandedKey] = useState(null)

	const coachID = coach?.coachID
	const seesAll = managesSchool(coach, selectedSchool)

	useEffect(() => {
		if (!selectedSchool || !coachID) return
		setDeletions(null)
		setFailed(false)
		return subscribeToLessonDeletions(
			selectedSchool,
			{ coachID, all: seesAll },
			setDeletions,
			err => {
				console.error("Lezioni eliminate non lette:", err)
				setFailed(true)
			},
		)
	}, [selectedSchool, coachID, seesAll])

	const school = getSelectedSchool()
	const nameOf = id => school?.coaches?.[id]?.coachName ?? id
	// i maestri del circolo, una volta caricati: prima sono reference
	const isInSchool = id => Boolean(!Array.isArray(school?.coaches) && school?.coaches?.[id])

	// La reinserisce chi potrebbe inserirla da capo: il suo maestro, o chi
	// ne tiene le lezioni. Non se il maestro non è più nel circolo, e senza
	// gli assistenti che ne sono usciti: le regole la rifiuterebbero.
	const reinsertable = group => {
		const lessonData = reinsertLessonData(group)
		if (!lessonData || !isInSchool(lessonData.coachID)) return null
		if (
			lessonData.coachID !== coachID &&
			!managesLessonsOf(coach, selectedSchool, lessonData.coachID)
		)
			return null
		return {
			...lessonData,
			assistantCoachIDs: lessonData.assistantCoachIDs.filter(isInSchool),
		}
	}

	// a un maestro interessano quelle che non ha tolto lui
	const visible = (deletions ?? []).filter(
		deletion => seesAll || deletion.deletedBy !== coachID,
	)
	const groups = groupLessonDeletions(visible)

	const deletedByText = group => {
		const when = group.deletedAt?.toDate ? `, ${formatEditTime(group.deletedAt.toDate())}` : ""
		if (!group.deletedBy) return `Eliminata${when}`
		if (group.deletedBy === coachID) return `Eliminata da te${when}`
		return `Eliminata da ${nameOf(group.deletedBy)}${when}`
	}

	const details = lesson =>
		[
			seesAll ? nameOf(lesson.coachID) : null,
			getLessonType(lesson.lessonType)?.lessonTypeName ?? null,
			lesson.court ? `Campo ${lesson.court.courtNumber ?? lesson.court}` : null,
			lesson.cancelled ? "Era annullata" : null,
		]
			.filter(Boolean)
			.join(" · ")

	const partecipantsText = lesson =>
		(lesson.partecipants ?? [])
			.filter(player => player.playerName?.trim())
			.map(player => {
				const method = getPaymentMethodName(getPaymentMethod, player.paymentMethod)
				const amount = player.paymentAmount ? `${formatAmount(player.paymentAmount)}€` : ""
				const payment = [method, amount].filter(Boolean).join(" ")
				return payment ? `${player.playerName.trim()} (${payment})` : player.playerName.trim()
			})
			.join(", ")

	const renderGroup = group => {
		const first = group.lessons[0]
		const isSeries = group.lessons.length > 1
		const isExpanded = expandedKey === group.key
		const people = partecipantsText(first)
		const lessonData = reinsertable(group)

		return (
			<Div
				p={{ y: "0.75rem" }}
				style={{ cursor: isSeries ? "pointer" : "default" }}
				onClick={() => isSeries && setExpandedKey(isExpanded ? null : group.key)}>
				<Text textWeight="bold" textColor={theme.colors.gunmetal}>
					{deletionTitle(group)}
				</Text>
				{details(first) && (
					<Text textSize="caption" textColor={theme.colors.text_light}>
						{details(first)}
					</Text>
				)}
				{people && (
					<Text textSize="caption" textColor={theme.colors.gunmetal}>
						{people}
					</Text>
				)}
				{first.lessonNotes && (
					<Text textSize="caption" textColor={theme.colors.text_light}>
						Note: {first.lessonNotes}
					</Text>
				)}
				{isSeries && (
					<Text textSize="caption" textColor={theme.colors.primary}>
						{isExpanded
							? group.lessons
									.map(lesson =>
										toDateTime(lesson.lessonStart)?.setLocale("it").toFormat("ccc d LLL"),
									)
									.filter(Boolean)
									.join(", ")
							: "Vedi le date"}
					</Text>
				)}
				<Div d="flex" justify="space-between" align="center" m={{ t: "0.25rem" }}>
					<Text textSize="caption" textColor={theme.colors.text_light}>
						{deletedByText(group)}
					</Text>
					{lessonData && (
						<Button
							h="2rem"
							p={{ x: "0.75rem" }}
							m={{ l: "0.5rem" }}
							bg={theme.colors.gunmetal_fill}
							rounded={theme.rounded["20px"]}
							style={{ gap: "0.25rem", flexShrink: 0 }}
							onClick={event => {
								// la voce di una serie si apre e si chiude toccandola
								event.stopPropagation()
								Modal.open("reinsertLesson", lessonData)
							}}>
							<FontAwesomeIcon icon={faRotateLeft} color={theme.colors.white} size="sm" />
							<Text tag="span" textSize="caption" textWeight="bold" textColor={theme.colors.white}>
								Reinserisci
							</Text>
						</Button>
					)}
				</Div>
			</Div>
		)
	}

	return (
		<>
			<Container>
				<Div m={{ b: "0.25rem" }} textWeight="bold" textSize="title">
					Lezioni eliminate
				</Div>
				<Text textSize="caption" textColor={theme.colors.text_light} m={{ b: "0.5rem" }}>
					{seesAll
						? "Le lezioni tolte da questo circolo nell'ultimo anno, e chi le ha tolte."
						: "Le tue lezioni tolte da un collega nell'ultimo anno."}{" "}
					Una lezione tolta per sbaglio si rimette con Reinserisci: si riapre già compilata, da
					controllare prima di salvare.
				</Text>
			</Container>

			<Div maxH="60vh" style={{ overflowY: "auto", overflowX: "hidden" }}>
				<Container>
					{failed ? (
						<Text textColor={theme.colors.text_light} p={{ y: "0.5rem" }}>
							Non sono riuscito a leggerle: riprova tra poco.
						</Text>
					) : deletions === null ? (
						<Text textColor={theme.colors.text_light} p={{ y: "0.5rem" }}>
							Un attimo…
						</Text>
					) : groups.length === 0 ? (
						<EmptyState
							icon={faTrashCan}
							title="Nessuna lezione eliminata"
							text={
								seesAll
									? "Quando qualcuno toglie una lezione del circolo, la ritrovi qui."
									: "Se un collega toglie una tua lezione, la ritrovi qui."
							}
						/>
					) : (
						groups.map((group, i) => (
							<Div key={group.key}>
								{renderGroup(group)}
								{i < groups.length - 1 && <Divider w="2px solid" />}
							</Div>
						))
					)}
				</Container>
			</Div>

			<Div d="flex" justify="center" m={{ t: "1rem", x: "1rem" }}>
				<SecondaryButton
					m="auto"
					textColor={theme.colors.gunmetal}
					textSize="subheader"
					text="Chiudi"
					action={closeModal}
				/>
			</Div>
		</>
	)
}

export default LessonDeletionsModal
